import { useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { PDA_PREFIX } from "../global/constants";
import { loadPersistedJobs, removePersistedJob } from "../services/ws/JobPersistenceService";
import { dismissCard } from "../services/progress-notifications/actions";
import { subscribeStomp } from "../services/stompClient";
import { normalizeWsStatus } from "../services/ws/wsFrameNormalizer";
import { getProgressCounts, getTerminalFromCounts, readEtaSeconds } from "../services/ws/AsyncJobService";
import { SCOPE_CSV_IMPORT_REPORT } from "../services/types";
import { extractImportReport } from "../components/ImportReport/importReport";

/**
 * Restores in-progress async job cards after a page refresh.
 *
 * Flow:
 *   1. AsyncJobService persists job metadata to localStorage on onJobStarted
 *      (with client UUID) and migrates to backend UUID after the 202 body.
 *   2. On mount, this hook reads localStorage:
 *      a. Backend UUID present → poll /async-job/{jobId}/status:
 *           RUNNING   → dispatch STARTED, re-subscribe WS topic
 *           COMPLETED → dispatch STARTED + terminal (auto-dismiss)
 *           FAILED    → dispatch STARTED + terminal (error card)
 *           404/403   → job expired, silently removed
 *      b. Client UUID (no 202 body yet, or fast refresh) → dispatch STARTED
 *         + re-subscribe to WS topic without polling (terminal frames handle cleanup)
 */

// Translates backend kind string → frontend kind used by Redux slices.
// AsyncJobService.js persists config.kind (backend) so we must convert.
const BACKEND_TO_FRONTEND_KIND = {
  "EXCEL_IMPORT":          "import",
  "PROJECT_DELETE":        "delete",
  "SCHEDULE_UPDATE":       "updateUsages",
  "SCENARIO_COPY":         "copy",
  "MPP_BATCH_IMPORT":      "mppBatchImport",
  "AI_IMPORT":             "aiImport",
  "SCENARIO_DELETE":       "scenarioDelete",
  "AUTO_FEED_ACTUALS":     "autoFeedActuals",
  "RESET_CAPACITY":        "resetCapacity",
  "STRUCTURE_CSV_IMPORT":  "scopeCsvImport",
  "PORTFOLIO_SIMULATION":  "portfolioSimulation",
  "GENERATIVE_SCHEDULING": "generativeScheduling",
  // Identity passthrough for forward compat
  "import":              "import",
  "delete":              "delete",
  "updateUsages":        "updateUsages",
  "copy":                "copy",
  "mppBatchImport":      "mppBatchImport",
  "aiImport":            "aiImport",
  "scenarioDelete":      "scenarioDelete",
  "autoFeedActuals":     "autoFeedActuals",
  "resetCapacity":       "resetCapacity",
  "scopeCsvImport":      "scopeCsvImport",
  "generativeScheduling":"generativeScheduling",
};

const KIND_STARTED_TYPES = {
  import:               "IMPORT_STARTED",
  delete:               "DELETE_STARTED",
  updateUsages:         "USAGES_STARTED",
  copy:                 "COPY_STARTED",
  mppBatchImport:       "MPP_IMPORT_STARTED",
  aiImport:             "AI_IMPORT_STARTED",
  scenarioDelete:       "SCENARIO_DELETE_JOB_STARTED",
  autoFeedActuals:      "AUTO_FEED_ACTUALS_STARTED",
  resetCapacity:        "RESET_CAPACITY_STARTED",
  scopeCsvImport:       "SCOPE_CSV_IMPORT_STARTED",
  generativeScheduling: "GENERATIVE_STARTED",
};

const KIND_TERMINAL_TYPES = {
  import:               { ok: "IMPORT_ALL_COMPLETED",              fail: "IMPORT_ALL_FAILED" },
  delete:               { ok: "DELETE_JOB_ALL_COMPLETED",          fail: "DELETE_JOB_ALL_FAILED" },
  updateUsages:         { ok: "USAGES_ALL_COMPLETED",              fail: "USAGES_ALL_FAILED" },
  copy:                 { ok: "COPY_JOB_ALL_COMPLETED",            fail: "COPY_JOB_ALL_FAILED" },
  mppBatchImport:       { ok: "MPP_IMPORT_ALL_COMPLETED",          fail: "MPP_IMPORT_ALL_FAILED" },
  aiImport:             { ok: "AI_IMPORT_ALL_COMPLETED",           fail: "AI_IMPORT_ALL_FAILED" },
  scenarioDelete:       { ok: "SCENARIO_DELETE_JOB_ALL_COMPLETED", fail: "SCENARIO_DELETE_JOB_ALL_FAILED" },
  autoFeedActuals:      { ok: "AUTO_FEED_ACTUALS_ALL_COMPLETED",   fail: "AUTO_FEED_ACTUALS_ALL_FAILED" },
  resetCapacity:        { ok: "RESET_CAPACITY_ALL_COMPLETED",      fail: "RESET_CAPACITY_ALL_FAILED" },
  scopeCsvImport:       { ok: "SCOPE_CSV_IMPORT_ALL_COMPLETED",     fail: "SCOPE_CSV_IMPORT_ALL_FAILED" },
  generativeScheduling: { ok: "GENERATIVE_ALL_COMPLETED",          fail: "GENERATIVE_ALL_FAILED" },
};

/** Regex to detect a v4 UUID — client-generated UUIDs vs backend UUIDs. */
const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** True if this looks like a backend-issued UUID (can be polled). */
function isBackendJobId(jobId) {
  // Both client and backend UUIDs are v4, but the backend UUID is confirmed
  // by persistJob being called with kind set. We can't distinguish them by
  // format alone — we distinguish by whether the entry was migrated.
  // For now, ALL UUIDs in localStorage are attempted: poll first, fall back
  // to WS-only if 404.
  return UUID_V4_RE.test(jobId);
}

/**
 * The library CSV import leaves its final report (counters + warnings, or
 * the row-level errors of a rejected file) on the job row / terminal frame;
 * hand it to the slice so the rehydrated card's "See details" still works.
 */
function dispatchImportReport(job, dispatch, source) {
  if (BACKEND_TO_FRONTEND_KIND[job.kind] !== "scopeCsvImport" && job.kind !== "scopeCsvImport") return;
  const report = extractImportReport(source);
  if (report) dispatch({ type: SCOPE_CSV_IMPORT_REPORT, payload: { jobId: job.jobId, report } });
}

function resubscribeToWs(job, dispatch, types, startedType) {
  if (!job.topic) return;
  const unsubscribe = subscribeStomp(job.topic, (message) => {
    let frame;
    try { frame = JSON.parse(message.body); } catch { return; }
    if (frame.jobId && frame.jobId !== job.jobId) return;

    const status = normalizeWsStatus(frame.status);
    if (status === "ALL_COMPLETED") {
      if (types) dispatch({ type: types.ok, payload: { jobId: job.jobId, terminatedAt: Date.now() } });
      dispatchImportReport(job, dispatch, frame);
      removePersistedJob(job.jobId);
      unsubscribe();
      return;
    }
    if (status === "ALL_FAILED") {
      if (types) dispatch({ type: types.fail, payload: { jobId: job.jobId, error: frame.error, terminatedAt: Date.now() } });
      dispatchImportReport(job, dispatch, frame);
      removePersistedJob(job.jobId);
      unsubscribe();
      return;
    }

    // Intermediate progress frame: feed updated counts + ETA into the slice via
    // a merge-STARTED dispatch so the card's ETA keeps ticking after a refresh.
    if (startedType) {
      const counts = getProgressCounts(frame);
      const eta = readEtaSeconds(frame);
      const payload = { jobId: job.jobId };
      if (counts.total != null) payload.totalProjects = counts.total;
      if (counts.completed != null) payload.completedCount = counts.completed;
      if (counts.failed != null) payload.failedCount = counts.failed;
      if (eta != null) payload.etaSeconds = eta;
      dispatch({ type: startedType, payload });
    }

    // BE intermittently drops the terminal frame under STOMP; derive it from
    // the counts so the card still resolves — but ONLY on item-terminal frames
    // (COMPLETED/FAILED), matching the live handler, so a non-terminal frame that
    // happens to carry cumulative counts can't spuriously finalize a running job.
    const terminal = getTerminalFromCounts(frame, status);
    if (terminal === "ALL_COMPLETED") {
      if (types) dispatch({ type: types.ok, payload: { jobId: job.jobId, terminatedAt: Date.now() } });
      removePersistedJob(job.jobId);
      unsubscribe();
    } else if (terminal === "ALL_FAILED") {
      if (types) dispatch({ type: types.fail, payload: { jobId: job.jobId, error: frame.error, terminatedAt: Date.now() } });
      removePersistedJob(job.jobId);
      unsubscribe();
    }
  });
}

export function useJobRehydration() {
  const dispatch = useDispatch();

  useEffect(() => {
    const jobs = loadPersistedJobs();
    if (!jobs.length) return;

    jobs.forEach((job) => {
      const frontendKind = BACKEND_TO_FRONTEND_KIND[job.kind] ?? job.kind;
      const startedType = KIND_STARTED_TYPES[frontendKind];
      const types = KIND_TERMINAL_TYPES[frontendKind];

      if (!startedType) {
        removePersistedJob(job.jobId);
        return;
      }

      // Show the card immediately with the last persisted progress data.
      // totalProjects, completedCount, etaSeconds are kept up-to-date by
      // the Redux store subscriber in store/store/index.js.
      dispatch({
        type: startedType,
        payload: {
          jobId:          job.jobId,
          topic:          job.topic,
          startedAt:      job.startedAt      ?? 0,
          fileName:       job.fileName       ?? null,
          label:          job.label          ?? null,
          totalProjects:  job.totalProjects  ?? 0,
          completedCount: job.completedCount ?? 0,
          failedCount:    job.failedCount    ?? 0,
          etaSeconds:     job.etaSeconds     ?? null,
        },
      });

      // Try polling to get the current status
      axios
        .get(`${PDA_PREFIX}/async-job/${job.jobId}/status`)
        .then((resp) => {
          const { status, error } = resp.data ?? {};
          const normalised = normalizeWsStatus(status);

          if (normalised === "ALL_COMPLETED") {
            if (types) dispatch({ type: types.ok, payload: { jobId: job.jobId, terminatedAt: Date.now() } });
            dispatchImportReport(job, dispatch, resp.data);
            removePersistedJob(job.jobId);
          } else if (normalised === "ALL_FAILED") {
            if (types) dispatch({ type: types.fail, payload: { jobId: job.jobId, error, terminatedAt: Date.now() } });
            dispatchImportReport(job, dispatch, resp.data);
            removePersistedJob(job.jobId);
          } else {
            // Still RUNNING — merge the registry's counts first: they can be
            // fresher than the persisted snapshot (a reload right after job
            // start persists 0/0, while the backend already knows the total),
            // and frames sent before the resubscribe below are gone for good.
            const { completedCount, totalProjects } = resp.data ?? {};
            const merge = { jobId: job.jobId };
            if (totalProjects != null && totalProjects > 0) merge.totalProjects = totalProjects;
            if (completedCount != null && completedCount > 0) merge.completedCount = completedCount;
            if (Object.keys(merge).length > 1) dispatch({ type: startedType, payload: merge });
            // Re-subscribe to the WS topic. Progress frames now flow back into
            // the slice via a non-destructive merge-STARTED, so there's no need
            // (and it would be destructive) to re-init here.
            resubscribeToWs(job, dispatch, types, startedType);
          }
        })
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 404 || status === 403) {
            // The backend has no record of this job (expired / unknown id), so it
            // can never emit a terminal frame — re-subscribing would leave the card
            // spinning forever. Silently discard the optimistic card and remove its
            // persisted entry so it doesn't resurface on the next load.
            dispatch(dismissCard(frontendKind, job.jobId));
          } else {
            // Transient failure (network / 5xx): fall back to WS-only — a terminal
            // frame will clean up the card when the job actually finishes.
            resubscribeToWs(job, dispatch, types, startedType);
          }
        });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
